// DiceController is the authoritative-client core: it drives the Game SDK's
// roll() action and the state machine, and exposes a view model the renderer
// subscribes to. It contains NO PixiJS imports so it is fully unit-testable. It
// NEVER fabricates dice, NEVER computes payout/balance/multiplier locally, and
// NEVER lets animation state start a new backend roll. The animation is purely a
// presentation of the saved server outcome.
import type {
  BalanceResult,
  ExchangeResult,
  RollOutcome,
  SpinResult,
} from '@aerofruits/game-sdk';
import { GameError, SessionExpiredError, ValidationError } from '@aerofruits/game-sdk';
import { StateMachine, type GameState } from '../state/machine.js';
import { parseLightningNumberResult, type LightningNumberSlot } from './lightning-types.js';
import type { I18n } from '@aerofruits/i18n';

/** Drives the compact round result block (presentation only). */
export interface ResultBlockPresenter {
  prepareRoll(): void;
  showHint(): void;
  hide(): void;
  showLoss(): void;
  showWin(params: {
    matchCount: number;
    baseProfitMultiplierTenths: number;
    lightningProfitMultiplierTenths: number;
    profitMultiplierTenths: number;
    profitAmountMinor: number;
    stakeReturnMultiplierTenths: number;
    stakeReturnAmountMinor: number;
    totalPayoutMultiplierTenths: number;
    totalPayoutAmountMinor: number;
    currency: string;
  }): void;
}

/** Drives the unified number row (presentation only). */
export interface LightningPresenter {
  prepareRoll(): void;
  revealLightning(slots: LightningNumberSlot[], reducedMotion: boolean): Promise<void>;
  setSelectedNumber(n: number | null): void;
  setMatchHighlights(dice: number[], selected: number): void;
  commitRound(slots: LightningNumberSlot[], dice: number[], selected: number): void;
  enableAfterRound(): void;
  restoreCompletedRound(): void;
  cancel(): void;
}

/** The subset of the SDK the controller depends on (injectable for tests). */
export interface DiceApi {
  exchangeLaunchToken(launchToken: string): Promise<ExchangeResult>;
  getBalance(): Promise<BalanceResult>;
  roll(params: { selectedNumber: number; betMinor: number; idempotencyKey?: string }): Promise<RollOutcome>;
  recoverAfterReload(): Promise<SpinResult | null>;
  closeSession(): Promise<unknown>;
}

/** Optional context for post-roll lightning-to-dice arcs (visual only). */
export interface RevealExtras {
  selectedNumber: number;
  selectedIsLightning: boolean;
  appliedLightningMultiplierTenths: number;
}

/** The renderer implements this to animate the authoritative dice result. */
export interface Animator {
  /** Pre-result anticipation (dice prepare to launch). */
  anticipate(): Promise<void>;
  /**
   * Animate the three dice and SETTLE on the exact server values, highlighting
   * matches. Receives ONLY authoritative data; it never derives the outcome.
   */
  reveal(
    dice: number[],
    matchFlags: boolean[],
    matchCount: number,
    multiplier: number,
    extras?: RevealExtras,
  ): Promise<void>;
  /** Cancels any in-flight animation (error recovery). */
  cancel?(): void;
}

export interface HistoryEntry {
  roundId: string;
  timestampMs: number;
  selectedNumber: number;
  dice: number[];
  betMinor: number;
  winMinor: number;
  multiplier: number;
  matchCount: number;
  /** The round's settlement currency, so each row formats independently. */
  currency: string;
  /** Public replay id for a persistent, shareable replay link (if available). */
  replayId?: string;
  /** Whether this round may be shared publicly (demo rounds by default). */
  replayPublic?: boolean;
  baseMultiplierTenths?: number;
  lightningMultiplierTenths?: number;
  totalMultiplierTenths?: number;
  lightningTier?: string;
}

export interface ViewModel {
  state: GameState;
  balanceMinor: number | null;
  balanceVersion: number;
  currency: string;
  betMinor: number;
  betOptions: readonly number[];
  /** The active selection, or null when the player has not picked a number yet. */
  selectedNumber: number | null;
  /** Derived: every condition required to start a roll is satisfied. */
  canRoll: boolean;
  lastRound: SpinResult | null;
  /** The authoritative dice currently on screen (after reveal), if any. */
  dice: number[] | null;
  matchCount: number | null;
  multiplier: number | null;
  lastWinMinor: number;
  message: string | null;
  errorCode: string | undefined;
  correlationId: string | undefined;
  history: readonly HistoryEntry[];
}

const noopAnimator: Animator = {
  anticipate: () => Promise.resolve(),
  reveal: () => Promise.resolve(),
  cancel: () => undefined,
};

export interface ControllerOptions {
  /** Built-in FALLBACK denominations (server-delivered list takes precedence). */
  betOptions: readonly number[];
  /** Preferred (e.g. persisted) bet; clamped to the allowed set. */
  initialBetMinor: number;
  /** Canonical fallback bet when the preferred bet is not allowed (default 100). */
  defaultBetMinor?: number;
  /** Preferred (e.g. persisted) selected number 1..6. Absent => nothing selected. */
  initialSelectedNumber?: number | null;
  animator?: Animator;
  lightning?: LightningPresenter;
  resultBlock?: ResultBlockPresenter;
  i18n?: I18n;
  formatMoney?: (minor: number, currency: string) => string;
  reducedMotion?: () => boolean;
}

function sanitizeDenominations(list: readonly number[]): number[] {
  const out = Array.from(new Set(list.filter((n) => Number.isInteger(n) && n > 0)));
  out.sort((a, b) => a - b);
  return out;
}

function clampFace(n: number): number {
  if (!Number.isInteger(n) || n < 1) return 1;
  if (n > 6) return 6;
  return n;
}

/** A valid die face (1..6) or null. Used for the persisted/initial selection. */
function normalizeFace(n: number | null | undefined): number | null {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 6 ? n : null;
}

export class DiceController {
  private readonly api: DiceApi;
  private readonly machine = new StateMachine('BOOTING');
  private animator: Animator;
  private readonly listeners = new Set<(vm: ViewModel) => void>();

  private balanceMinor: number | null = null;
  private balanceVersion = 0;
  private currency = '';
  private betMinor: number;
  private betOptions: number[];
  private readonly fallbackBetMinor: number;
  /** null until the player picks a number; gates rolling (see canRoll()). */
  private selectedNumber: number | null;
  private lastRound: SpinResult | null = null;
  private dice: number[] | null = null;
  private matchCount: number | null = null;
  private multiplier: number | null = null;
  private lastWinMinor = 0;
  private message: string | null = null;
  private errorCode: string | undefined;
  private correlationId: string | undefined;
  private readonly history: HistoryEntry[] = [];
  /** Key of the in-flight roll, so a retry reuses the SAME key (no reroll). */
  private pendingRollKey: string | undefined;
  private readonly lightning: LightningPresenter | undefined;
  private readonly resultBlock: ResultBlockPresenter | undefined;
  private readonly i18n: I18n | undefined;
  private readonly formatMoney: ((minor: number, currency: string) => string) | undefined;
  private readonly reducedMotionFn: () => boolean;

  constructor(api: DiceApi, opts: ControllerOptions) {
    this.api = api;
    this.betOptions = sanitizeDenominations(opts.betOptions);
    this.fallbackBetMinor = opts.defaultBetMinor ?? 100;
    this.betMinor = this.pickBet(opts.initialBetMinor);
    this.selectedNumber = normalizeFace(opts.initialSelectedNumber);
    this.animator = opts.animator ?? noopAnimator;
    this.lightning = opts.lightning;
    this.resultBlock = opts.resultBlock;
    this.i18n = opts.i18n;
    this.formatMoney = opts.formatMoney;
    this.reducedMotionFn = opts.reducedMotion ?? (() => false);
    this.machine.subscribe(() => this.emit());
  }

  private pickBet(preferred: number): number {
    if (this.betOptions.includes(preferred)) return preferred;
    if (this.betOptions.includes(this.fallbackBetMinor)) return this.fallbackBetMinor;
    return this.betOptions[0] ?? 0;
  }

  /** Adopts the SERVER-authoritative denomination set (from launch/exchange). */
  adoptDenominations(serverList: readonly number[] | null | undefined): void {
    const next = sanitizeDenominations(serverList ?? []);
    if (next.length === 0) return;
    this.betOptions = next;
    this.betMinor = this.pickBet(this.betMinor);
    this.emit();
  }

  setAnimator(a: Animator): void {
    this.animator = a;
  }

  state(): GameState {
    return this.machine.current();
  }

  /**
   * Single source of truth for whether a roll may start. Every entry point (the
   * Roll button's enabled state AND the roll() handler's defensive guard) derives
   * from this, so the UI and the action can never disagree. A roll requires: the
   * machine idle (READY, which also blocks double-submit while a round runs), a
   * valid selected number, a valid server-authoritative bet, a known balance, and
   * enough balance to cover the bet.
   */
  canRoll(): boolean {
    return (
      this.machine.current() === 'READY' &&
      this.selectedNumber !== null &&
      this.betOptions.includes(this.betMinor) &&
      this.balanceMinor !== null &&
      this.balanceMinor >= this.betMinor
    );
  }

  viewModel(): ViewModel {
    return {
      state: this.machine.current(),
      balanceMinor: this.balanceMinor,
      balanceVersion: this.balanceVersion,
      currency: this.currency,
      betMinor: this.betMinor,
      betOptions: this.betOptions,
      selectedNumber: this.selectedNumber,
      canRoll: this.canRoll(),
      lastRound: this.lastRound,
      dice: this.dice,
      matchCount: this.matchCount,
      multiplier: this.multiplier,
      lastWinMinor: this.lastWinMinor,
      message: this.message,
      errorCode: this.errorCode,
      correlationId: this.correlationId,
      history: this.history,
    };
  }

  subscribe(l: (vm: ViewModel) => void): () => void {
    this.listeners.add(l);
    l(this.viewModel());
    return () => this.listeners.delete(l);
  }

  beginLoading(): void {
    this.machine.transition('LOADING');
  }

  /** Exchanges the launch token then attempts recovery of any unresolved round. */
  async start(launchToken: string): Promise<void> {
    // Tolerate being called before beginLoading() (e.g. in tests): walk through
    // the LOADING phase so the EXCHANGING_TOKEN transition is always legal.
    if (this.machine.current() === 'BOOTING') this.machine.transition('LOADING');
    this.machine.transition('EXCHANGING_TOKEN');
    let session: ExchangeResult;
    try {
      session = await this.api.exchangeLaunchToken(launchToken);
    } catch (err) {
      this.handleError(err, 'EXCHANGING_TOKEN');
      return;
    }
    this.currency = session.currency;
    this.adoptDenominations(session.bet_denominations);
    this.emit();
    await this.refreshBalance();
    await this.recover('EXCHANGING_TOKEN');
  }

  /** Fetches and displays the server-authoritative balance. Best-effort. */
  async refreshBalance(): Promise<void> {
    try {
      const b = await this.api.getBalance();
      this.balanceMinor = b.balance_minor;
      this.balanceVersion = b.balance_version;
      if (b.currency) this.currency = b.currency;
      this.emit();
    } catch (err) {
      if (err instanceof SessionExpiredError || (err instanceof GameError && err.kind === 'unauthorized')) {
        this.handleError(err, this.machine.current());
      }
    }
  }

  /** Recovers any unresolved round; safe to call after a reload. Never rolls. */
  async recover(from: GameState = this.machine.current()): Promise<void> {
    if (from !== 'RECOVERING') this.machine.transition('RECOVERING');
    let round: SpinResult | null;
    try {
      round = await this.api.recoverAfterReload();
    } catch (err) {
      this.handleError(err, 'RECOVERING');
      return;
    }
    if (round) {
      this.message = 'Resuming your previous round';
      await this.present(round, { animate: false });
      return;
    }
    this.message = null;
    this.machine.transition('READY');
  }

  /**
   * Player-initiated roll. Defensive guard: returns immediately unless every
   * precondition holds (no selection, invalid number/bet, insufficient balance,
   * or a round already in progress). Because READY is part of canRoll() and the
   * transition to ROLL_REQUESTED happens synchronously before any await, a second
   * click/tap or a duplicate keyboard submit is a no-op — no second network call,
   * no second round, and the in-flight idempotency key is reused on retry.
   */
  async roll(): Promise<void> {
    if (!this.canRoll()) return;
    const selectedNumber = this.selectedNumber as number; // canRoll() guarantees non-null
    this.errorCode = undefined;
    this.correlationId = undefined;
    this.message = null;
    try {
      this.machine.transition('ROLL_REQUESTED');
      this.machine.transition('WAITING_FOR_RESULT');
      this.resultBlock?.prepareRoll();
      this.lightning?.prepareRoll();
      try {
        await this.animator.anticipate();
      } catch (err) {
        this.failRound(err, this.machine.current());
        return;
      }

      let outcome: RollOutcome;
      try {
        const params: { selectedNumber: number; betMinor: number; idempotencyKey?: string } = {
          selectedNumber,
          betMinor: this.betMinor,
        };
        if (this.pendingRollKey !== undefined) params.idempotencyKey = this.pendingRollKey;
        outcome = await this.api.roll(params);
      } catch (err) {
        this.failRound(err, 'WAITING_FOR_RESULT');
        return;
      }
      this.pendingRollKey = undefined;
      await this.present(outcome.result, { animate: true });
    } catch (err) {
      this.failRound(err, this.machine.current());
    }
  }

  /** Selects a number 1..6. Allowed only when idle (never mid-round). */
  selectNumber(n: number): boolean {
    if (this.machine.isBusy()) return false;
    if (!Number.isInteger(n) || n < 1 || n > 6) return false;
    if (n === this.selectedNumber) return true;
    this.selectedNumber = n;
    this.lightning?.setSelectedNumber(n);
    this.emit();
    return true;
  }

  /** Changes the bet to an exact allowed denomination. No-op while busy. */
  changeBet(betMinor: number): boolean {
    if (this.machine.isBusy()) return false;
    if (!this.betOptions.includes(betMinor)) return false;
    this.betMinor = betMinor;
    this.emit();
    return true;
  }

  nextBet(): number {
    return this.stepBet(1);
  }

  prevBet(): number {
    return this.stepBet(-1);
  }

  maxBet(): number {
    if (this.machine.isBusy()) return this.betMinor;
    const max = this.betOptions[this.betOptions.length - 1];
    if (max !== undefined && max !== this.betMinor) {
      this.betMinor = max;
      this.emit();
    }
    return this.betMinor;
  }

  private stepBet(dir: 1 | -1): number {
    if (this.machine.isBusy()) return this.betMinor;
    const i = this.betOptions.indexOf(this.betMinor);
    if (i < 0) {
      this.betMinor = this.pickBet(this.betMinor);
      this.emit();
      return this.betMinor;
    }
    const j = Math.min(this.betOptions.length - 1, Math.max(0, i + dir));
    const next = this.betOptions[j];
    if (next !== undefined && next !== this.betMinor) {
      this.betMinor = next;
      this.emit();
    }
    return this.betMinor;
  }

  async close(): Promise<void> {
    try {
      await this.api.closeSession();
    } finally {
      // leave state as-is; caller may navigate away
    }
  }

  /** Drives the authoritative presentation. NEVER derives outcome from visuals. */
  private async present(result: SpinResult, opts: { animate: boolean }): Promise<void> {
    if (!result.round_id) {
      this.failRound(new GameError('validation', 'malformed result: missing round_id'), this.machine.current());
      return;
    }
    const detail = parseLightningNumberResult(result);
    if (!detail) {
      this.failRound(new GameError('validation', 'malformed lightning-number-dice result'), this.machine.current());
      return;
    }
    this.lastRound = result;
    this.applyAuthoritative(result, detail);

    const dice = detail.dice;
    const selected = detail.selectedNumber;
    const matchFlags = dice.map((d) => d === selected);
    const matchCount = detail.matchCount;
    this.dice = dice;
    this.matchCount = matchCount;
    this.multiplier = detail.totalPayoutMultiplierTenths;

    const revealExtras: RevealExtras = {
      selectedNumber: selected,
      selectedIsLightning: detail.selectedIsLightning,
      appliedLightningMultiplierTenths: detail.appliedLightningMultiplierTenths,
    };

    try {
      this.machine.transition('ANIMATING');
      if (opts.animate) {
        await this.lightning?.revealLightning(detail.lightningSlots, this.reducedMotionFn());
        await this.animator.reveal(dice, matchFlags, matchCount, detail.profitMultiplierTenths, revealExtras);
      } else {
        await this.lightning?.revealLightning(detail.lightningSlots, true);
        await this.animator.reveal(dice, matchFlags, matchCount, detail.profitMultiplierTenths, revealExtras);
      }
      this.lightning?.setMatchHighlights(dice, selected);

      if (matchCount === 0) {
        this.resultBlock?.showLoss();
      } else {
        this.resultBlock?.showWin({
          matchCount,
          baseProfitMultiplierTenths: detail.baseProfitMultiplierTenths,
          lightningProfitMultiplierTenths: detail.lightningProfitMultiplierTenths,
          profitMultiplierTenths: detail.profitMultiplierTenths,
          profitAmountMinor: detail.profitAmountMinor,
          stakeReturnMultiplierTenths: detail.stakeReturnMultiplierTenths,
          stakeReturnAmountMinor: detail.stakeReturnAmountMinor,
          totalPayoutMultiplierTenths: detail.totalPayoutMultiplierTenths,
          totalPayoutAmountMinor: detail.totalPayoutAmountMinor,
          currency: this.currency,
        });
      }
      this.lightning?.commitRound(detail.lightningSlots, dice, selected);
      this.lightning?.enableAfterRound();

      if (this.lastWinMinor > 0) {
        this.machine.transition('SHOWING_WIN');
      }
      this.message = null;
      this.machine.transition('READY');
    } catch (err) {
      this.failRound(err, this.machine.current());
    }
  }

  /** Aborts in-flight presentation; restores last completed round display when available. */
  private abortRoundPresentation(): void {
    this.lightning?.cancel();
    this.animator.cancel?.();
  }

  /** Restores READY after any round failure; never leaves controls locked. */
  private failRound(err: unknown, from: GameState): void {
    this.abortRoundPresentation();
    this.handleError(err, from);
    if (this.machine.current() === 'ERROR' && this.machine.canTransition('READY')) {
      this.machine.transition('READY');
    }
    if (this.isPlayerFacingRoundError(err)) {
      this.message = this.i18n?.t('lnd.errors.roundFailed') ?? 'Round failed. Please try again.';
      if (err instanceof GameError || err instanceof ValidationError) {
        console.warn('round failed', err);
      }
    }
    this.resultBlock?.showHint();
    this.emit();
  }

  private isPlayerFacingRoundError(err: unknown): boolean {
    if (err instanceof SessionExpiredError) return false;
    if (err instanceof GameError && (err.kind === 'unauthorized' || err.kind === 'conflict')) return false;
    return true;
  }

  private applyAuthoritative(result: SpinResult, detail: NonNullable<ReturnType<typeof parseLightningNumberResult>>): void {
    if (typeof result.balance_after_minor === 'number') this.balanceMinor = result.balance_after_minor;
    if (typeof result.balance_version === 'number' && result.balance_version > 0) {
      this.balanceVersion = result.balance_version;
    }
    if (result.currency) this.currency = result.currency;
    this.lastWinMinor = detail.totalPayoutAmountMinor;
    if (result.selected_number) this.selectedNumber = clampFace(result.selected_number);
    this.lightning?.setSelectedNumber(this.selectedNumber);
    const entry: HistoryEntry = {
      roundId: result.round_id,
      timestampMs: Date.now(),
      selectedNumber: detail.selectedNumber,
      dice: [...detail.dice],
      betMinor: result.total_bet_minor,
      winMinor: detail.totalPayoutAmountMinor,
      multiplier: detail.totalPayoutMultiplierTenths,
      matchCount: detail.matchCount,
      currency: result.currency || this.currency,
      baseMultiplierTenths: detail.baseProfitMultiplierTenths,
      lightningMultiplierTenths: detail.appliedLightningMultiplierTenths,
      totalMultiplierTenths: detail.totalPayoutMultiplierTenths,
      ...(result.replay_id ? { replayId: result.replay_id } : {}),
      ...(result.replay_public !== undefined ? { replayPublic: result.replay_public } : {}),
    };
    this.history.unshift(entry);
    if (this.history.length > 50) this.history.length = 50;
    this.emit();
  }

  private handleError(err: unknown, from: GameState): void {
    if (err instanceof SessionExpiredError || (err instanceof GameError && err.kind === 'unauthorized')) {
      this.message = 'Your session has expired. Please relaunch the game.';
      this.safeTransition(from, 'SESSION_EXPIRED');
      return;
    }
    if (err instanceof GameError && err.kind === 'conflict') {
      // A round is already in flight; do not start a new one — recover it.
      this.message = 'Resolving your round…';
      this.safeTransition(from, 'READY');
      void this.recover('READY');
      return;
    }
    if (err instanceof GameError) {
      this.errorCode = err.code ?? err.kind;
      this.correlationId = err.correlationId;
      this.message = err.message;
    } else {
      this.errorCode = 'unknown';
      this.message = 'Something went wrong';
    }
    this.safeTransition(from, 'ERROR');
  }

  private safeTransition(_from: GameState, to: GameState): void {
    if (this.machine.canTransition(to)) this.machine.transition(to);
    else this.emit();
  }

  private emit(): void {
    const vm = this.viewModel();
    for (const l of this.listeners) l(vm);
  }
}
